import { z } from 'zod';
import { jObjectLittle, Rights, ValueOf } from '../../types';
import { zObject } from '../../util/zod';

type ApplicationActionBase = {
    action: string;
    route?: string;
    nameApp?: string;
};

export const applicationActionBaseSchema = zObject<ApplicationActionBase>({
    action: z.string(),
    route: z.string().optional(),
    nameApp: z.string().optional(),
});

type ApplicationBase = {
    name: string;
    label: string;
    icon: string;
    img: string;
    url: string;
    description: string;
    color: string;
    action: ApplicationActionBase;
};

export const applicationBaseSchema = zObject<ApplicationBase>({
    name: z.string(),
    label: z.string(),
    icon: z.string(),
    img: z.string(),
    url: z.string(),
    description: z.string(),
    color: z.string(),
    action: applicationActionBaseSchema,
});

type ApplicationExtraProperties = {
    hasLicense?: boolean;
    sref?: string;
    type?: string;
    order?: number;
    checkAccess?: boolean;
    admin?: boolean;
    showTab?: boolean;
    types?: string[];
    noNotificationMenu?: boolean;
    attrExposed?: string[];
    serviceUrl?: string;
    view?: string;
    fieldAddress?: string;
    _extend?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
};
export const applicationExtraPropertiesSchema = zObject<ApplicationExtraProperties>({
    hasLicense: z.boolean().optional(),
    sref: z.string().optional(),
    type: z.string().optional(),
    order: z.number().optional(),
    checkAccess: z.boolean().optional(),
    admin: z.boolean().optional(),
    showTab: z.boolean().optional(),
    types: z.string().array().optional(),
    noNotificationMenu: z.boolean().optional(),
    attrExposed: z.string().array().optional(),
    serviceUrl: z.string().optional(),
    view: z.string().optional(),
    fieldAddress: z.string().optional(),
    _extend: z.record(z.string(), z.any()).optional(),
});

export type ApplicationDefault = ApplicationBase & ApplicationExtraProperties;
export const applicationDefaultSchema = zObject<ApplicationBase & ApplicationExtraProperties>({
    ...applicationBaseSchema.shape,
    ...applicationExtraPropertiesSchema.shape,
});

export type ApplicationLink = ApplicationBase & {
    urlTarget: '_blank';
    urlNoReferrer: boolean;
};
export const applicationLinkSchema = zObject<ApplicationBase & ApplicationLink>({
    ...applicationBaseSchema.shape,
    urlTarget: z.literal('_blank'),
    urlNoReferrer: z.boolean(),
});

export type ApplicationIframe = ApplicationBase & {
    iframeUrl: string;
};
export const applicationIframeSchema = zObject<ApplicationBase & ApplicationIframe>({
    ...applicationBaseSchema.shape,
    iframeUrl: z.string(),
});

export type ApplicationTab = {
    tab: string;
    name: string;
    url: string;
};
export const applicationTabSchema = zObject<ApplicationTab>({
    tab: z.string(),
    name: z.string(),
    url: z.string(),
});
const applicationsTabs = z.array(applicationTabSchema);
export const ensureApplicationTabs = (tabs: unknown): tabs is ApplicationTab[] => {
    return applicationsTabs.safeParse(tabs).success;
};

export type ApplicationWithTabs = ApplicationBase & {
    tabs?: Array<ApplicationTab>;
};
export const applicationWithTabsSchema = zObject<ApplicationBase & ApplicationWithTabs>({
    ...applicationBaseSchema.shape,
    tabs: applicationsTabs.optional(),
});

export type ApplicationIntranetHook = Omit<ApplicationBase, 'name'> & {
    name: 'IntranetPageHook';
    tabs: string[];
    releaseName: string;
};
export const applicationIntranetHookSchema = zObject<ApplicationIntranetHook>({
    ...applicationBaseSchema.omit({ name: true }).shape,
    name: z.literal('IntranetPageHook'),
    tabs: z.string().array(),
    releaseName: z.string(),
});

export type OAuth2ServerHook = Omit<ApplicationBase, 'name'> & {
    name: 'OAuth2ServerHook';
    tabs: Record<string, { tab: string; name?: string | undefined; url?: string | undefined }>;
    releaseName: string;
};
export const OAuth2ServerHookSchema = zObject<OAuth2ServerHook>({
    ...applicationBaseSchema.omit({ name: true }).shape,
    name: z.literal('OAuth2ServerHook'),
    tabs: z.record(z.string(), z.object({ tab: z.string(), name: z.string().optional(), url: z.string().optional() })),
    releaseName: z.string(),
});

export type ApplicationType =
    | ApplicationDefault
    | ApplicationLink
    | ApplicationIframe
    | ApplicationWithTabs
    | ApplicationIntranetHook
    | OAuth2ServerHook;
export const applicationSchema = z.union([
    applicationDefaultSchema,
    applicationLinkSchema,
    applicationIframeSchema,
    applicationWithTabsSchema,
    applicationIntranetHookSchema,
    OAuth2ServerHookSchema,
]);
export const ensureApplication = (a: unknown): a is ApplicationType => {
    return applicationSchema.safeParse(a).success;
};

export type StudioAction = {
    action: string;
    nameApp: string;
};

export interface AccessRightObject extends jObjectLittle {
    firstname?: string;
    lastname?: string;
    mail?: string;
    privacy?: string;
    category?: string;
    description?: string;
    image?: string | null;
    _values: {
        firstname?: string;
        lastname?: string;
        mail?: string;
        title: string;
        dateCreation: string;
        dateModified: string;
        privacy?: number;
        category?: number | string;
    };
    _cssColor: string;
    _cssClass: string;
    _right: Rights;
}

export type StudioApplicationBase = {
    idApp: string;
    type?: string;
    name?: string;
    label?: string;
    description?: string;
    checkAccess?: boolean;
    attrExposed?: Array<string>;
    status: StudioApplicationStatusEnumBased;
    suspended?: boolean;
    view?: string;
    author?: string;
    columns?: Record<string, string>;
    accessRightList?: string[];
    accessRightObjectList?: Array<AccessRightObject>;
    typeModel?: unknown;
    value?: string;
    manifest?: StudioManifest;
    img?: string;
    icon?: string;
    isAvailableOnMobile?: boolean;
    color?: string;
    url?: string;
    types?: string;
    urlTarget?: '_blank';
    order?: number;
    dateCreation?: string;
    studioVersion?: number;
    showImport?: boolean;
    showMap?: boolean;
    mail2RSE?: boolean;
    action?: StudioAction;
    display?: string;
};

export type StudioManifest = {
    dateCreation: string;
    author: string;
    manifestVersion: number;
    version: number;
    editor?: string;
    editorUrl?: string;
    categories?: string;
};

export enum StudioApplicationStatus {
    local = 'local',
    installed = 'installed',
    autosaved = 'autosaved',
    saved = 'saved',
}

export type StudioApplicationStatusEnumBased = ValueOf<StudioApplicationStatus>;

export type StudioApplication = StudioApplicationBase & {};
